/* RESPONSIBLE TEAM: team-ai-insights */
import { type LogicalFilter, type Filter } from 'embercom/components/reporting/custom/filters';
import { and } from 'embercom/components/reporting/automation/ai-insights/fin-insights/sankey-config';
import { type Aggregation } from 'embercom/objects/reporting/unified/metrics/types';
import type IntlService from 'ember-intl/services/intl';
import type { SummaryTab } from './conversation-explore-panel';

export interface ChartSeriesDefinitionWithoutLabel {
  metric_id: string;
  aggregation?: Aggregation;
  tooltip?: string;
  filters?: LogicalFilter | Filter;
}

export interface ChartSeriesDefinition extends ChartSeriesDefinitionWithoutLabel {
  label: string;
}

export function insightsMetrics(intl: IntlService): Record<string, ChartSeriesDefinition> {
  return {
    volume: {
      metric_id: 'v1.new_conversations',
      aggregation: 'count',
      label: intl.t('components.reporting.automation.fin-insights.new-conversations'),
    },
    resolutionRate: {
      metric_id: 'fin.resolution_rate',
      label: intl.t('components.reporting.automation.fin-insights.resolution-rate'),
    },
    involvementRate: {
      metric_id: 'fin.participation_rate',
      label: intl.t('components.reporting.automation.fin-insights.involvement-rate'),
    },
    involvedConversations: {
      metric_id: 'fin.conversations_participated.count',
      label: intl.t('components.reporting.automation.fin-insights.involved-conversations'),
    },
    finOnlyRatedConversations: {
      metric_id:
        'conversation.ai_generated_metrics.csat.count.first_user_conversation_part_created_at',
      label: intl.t('components.reporting.automation.fin-insights.fin-only-conversations'),
      filters: {
        type: 'and',
        filters: [
          {
            type: 'category',
            data: {
              property: 'agent_types_involved_grouping',
              attribute: 'conversation.agent_types_involved_grouping',
              values: ['ai-agent'],
            },
          },
        ],
      },
    },
  };
}

function cxFilter(values: string[]) {
  return {
    type: 'category',
    data: {
      property: 'conversation_custom_fields#customer_experience_attributes',
      attribute: 'conversation_custom_fields#customer_experience_attributes',
      values,
    },
  };
}

function escalationFilter(values: string[]) {
  return {
    type: 'category',
    data: {
      property: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      attribute: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      values,
    },
  };
}

function involvementFilter(values: string[]) {
  return {
    type: 'category',
    data: {
      property: 'conversation_custom_fields#ai_insights_fin_not_involved_reason',
      attribute: 'conversation_custom_fields#ai_insights_fin_not_involved_reason',
      values,
    },
  };
}

const filters = {
  get contentGaps() {
    return escalationFilter(['content_gap']);
  },
  get dataGaps() {
    return escalationFilter(['data_gap']);
  },
  get actionGaps() {
    return escalationFilter(['action_gap']);
  },
  get investigationRequired() {
    return escalationFilter(['investigation_needed']);
  },
  get businessLogic() {
    return escalationFilter(['business_logic']);
  },
  get customerDistrust() {
    return escalationFilter(['customer_distrust']);
  },
  get negativeAiAgentAnswerQuality() {
    return cxFilter(['answer_quality_bot:low']);
  },
  get highCustomerEffort() {
    return cxFilter(['user_effort:high']);
  },
  get negativePolicyFeedback() {
    return cxFilter(['policy_feedback:negative']);
  },
  get negativeProductFeedback() {
    return cxFilter(['product_feedback:negative']);
  },
  get negativeStrongEmotions() {
    return cxFilter(['user_strong_emotion:negative']);
  },
  get channelGaps() {
    return involvementFilter(['channel']);
  },
  get audienceTargeting() {
    return involvementFilter(['audience_targeting']);
  },
  get scheduling() {
    return involvementFilter(['scheduling']);
  },
  get branchingLogic() {
    return involvementFilter(['branching_logic']);
  },
};

function jobDefinitions(intl: IntlService) {
  let metrics = insightsMetrics(intl);

  return [
    {
      id: 'contentGaps',
      label: 'components.reporting.automation.ai-insights.optimize.jobs.content-gaps.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.content-gaps.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-green-50',
      chartSeries: [metrics.involvedConversations],
      filters: and(filters.contentGaps),
      attributesValues: ['content_gap'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_topic',
      summaryType: 'resolution_rate_drivers',
    },
    {
      id: 'dataGaps',
      label: 'components.reporting.automation.ai-insights.optimize.jobs.data-gaps.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.data-gaps.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-green-50',
      chartSeries: [metrics.involvedConversations],
      filters: and(filters.dataGaps),
      attributesValues: ['data_gap'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_topic',
      summaryType: 'resolution_rate_drivers',
    },
    {
      id: 'actionGaps',
      label: 'components.reporting.automation.ai-insights.optimize.jobs.action-gaps.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.action-gaps.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-green-50',
      chartSeries: [metrics.involvedConversations],
      filters: and(filters.actionGaps),
      attributesValues: ['action_gap'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_topic',
      summaryType: 'resolution_rate_drivers',
    },
    {
      id: 'investigationRequired',
      label:
        'components.reporting.automation.ai-insights.optimize.jobs.investigation-required.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.investigation-required.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-green-50',
      chartSeries: [metrics.involvedConversations],
      filters: and(filters.investigationRequired),
      attributesValues: ['investigation_needed'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_topic',
      summaryType: 'resolution_rate_drivers',
    },
    {
      id: 'businessLogic',
      label: 'components.reporting.automation.ai-insights.optimize.jobs.business-logic.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.business-logic.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-green-50',
      chartSeries: [metrics.involvedConversations],
      filters: and(filters.businessLogic),
      attributesValues: ['business_logic'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_topic',
      summaryType: 'resolution_rate_drivers',
    },
    {
      id: 'customerDistrust',
      label: 'components.reporting.automation.ai-insights.optimize.jobs.customer-distrust.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.customer-distrust.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-green-50',
      chartSeries: [metrics.involvedConversations],
      filters: and(filters.customerDistrust),
      attributesValues: ['customer_distrust'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_escalation_reason',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_topic',
      summaryType: 'resolution_rate_drivers',
    },
    {
      id: 'negativeAiAgentAnswerQuality',
      label:
        'components.reporting.automation.ai-insights.optimize.jobs.low-fin-answer-quality.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.low-fin-answer-quality.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-violet-50',
      chartSeries: [metrics.finOnlyRatedConversations],
      filters: and(filters.negativeAiAgentAnswerQuality),
      attributesValues: ['answer_quality_bot:low'],
      attributeId: 'conversation_custom_fields#customer_experience_attributes',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_topic',
      summaryType: 'cxscore_drivers',
    },
    {
      id: 'highCustomerEffort',
      label: 'components.reporting.automation.ai-insights.optimize.jobs.high-customer-effort.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.high-customer-effort.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-violet-50',
      chartSeries: [metrics.finOnlyRatedConversations],
      filters: and(filters.highCustomerEffort),
      attributesValues: ['user_effort:high'],
      attributeId: 'conversation_custom_fields#customer_experience_attributes',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_topic',
      summaryType: 'cxscore_drivers',
    },
    {
      id: 'negativeProductFeedback',
      label:
        'components.reporting.automation.ai-insights.optimize.jobs.negative-product-feedback.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.negative-product-feedback.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-violet-50',
      chartSeries: [metrics.finOnlyRatedConversations],
      filters: and(filters.negativeProductFeedback),
      attributesValues: ['product_feedback:negative'],
      attributeId: 'conversation_custom_fields#customer_experience_attributes',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_topic',
      summaryType: 'cxscore_drivers',
    },
    {
      id: 'negativePolicyFeedback',
      label:
        'components.reporting.automation.ai-insights.optimize.jobs.negative-policy-feedback.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.negative-policy-feedback.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-violet-50',
      chartSeries: [metrics.finOnlyRatedConversations],
      filters: and(filters.negativePolicyFeedback),
      attributesValues: ['policy_feedback:negative'],
      attributeId: 'conversation_custom_fields#customer_experience_attributes',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_topic',
      summaryType: 'cxscore_drivers',
    },
    {
      id: 'negativeStrongEmotions',
      label:
        'components.reporting.automation.ai-insights.optimize.jobs.negative-strong-emotions.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.negative-strong-emotions.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-violet-50',
      chartSeries: [metrics.finOnlyRatedConversations],
      filters: and(filters.negativeStrongEmotions),
      attributesValues: ['user_strong_emotion:negative'],
      attributeId: 'conversation_custom_fields#customer_experience_attributes',
      breakdownByAttributeId: 'conversation_custom_fields#ai_insights_topic',
      summaryType: 'cxscore_drivers',
    },
    {
      id: 'channelGaps',
      label: 'components.reporting.automation.ai-insights.optimize.jobs.channel-gaps.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.channel-gaps.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-blue-50',
      chartSeries: [metrics.volume],
      filters: and(filters.channelGaps),
      attributesValues: ['channel'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_not_involved_reason',
      breakdownByAttributeId: 'channel_type',
      summaryType: 'issue_summaries',
    },
    {
      id: 'audienceTargeting',
      label: 'components.reporting.automation.ai-insights.optimize.jobs.audience-targeting.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.audience-targeting.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-blue-50',
      chartSeries: [metrics.volume],
      filters: and(filters.audienceTargeting),
      attributesValues: ['audience_targeting'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_not_involved_reason',
      breakdownByAttributeId: 'channel_type',
      summaryType: 'issue_summaries',
    },
    {
      id: 'scheduling',
      label: 'components.reporting.automation.ai-insights.optimize.jobs.scheduling.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.scheduling.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-blue-50',
      chartSeries: [metrics.volume],
      filters: and(filters.scheduling),
      attributesValues: ['scheduling'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_not_involved_reason',
      breakdownByAttributeId: 'channel_type',
      summaryType: 'issue_summaries',
    },
    {
      id: 'branchingLogic',
      label: 'components.reporting.automation.ai-insights.optimize.jobs.branching-logic.label',
      description:
        'components.reporting.automation.ai-insights.optimize.jobs.branching-logic.description',
      showSuggestions: false,
      backgroundClass: 'bg-vis-blue-50',
      chartSeries: [metrics.volume],
      filters: and(filters.branchingLogic),
      attributesValues: ['branching_logic'],
      attributeId: 'conversation_custom_fields#ai_insights_fin_not_involved_reason',
      breakdownByAttributeId: 'channel_type',
      summaryType: 'issue_summaries',
    },
  ];
}

type JobId = ReturnType<typeof jobDefinitions>[number]['id'];

interface Job {
  id: JobId;
  label: string;
  description: string;
  showSuggestions: boolean;
  backgroundClass: string;
  chartSeries: readonly ChartSeriesDefinition[];
  filters: LogicalFilter;
  attributesValues: readonly string[];
  attributeId: string;
  breakdownByAttributeId?: string;
  summaryType: SummaryTab;
}

interface JobGroupObject {
  id: string;
  label: string;
  header?: string;
  chartSeries: ChartSeriesDefinitionWithoutLabel[];
  backgroundClass: string;
  borderClass: string;
  fontClass: string;
  positiveLabel: string;
  negativeLabel: string;
  positiveTextColor: string;
  negativeTextColor: string;
  positiveBackgroundClass: string;
  negativeBackgroundClass: string;
}

const jobGroups: Record<string, readonly JobId[]> = {
  improveAnswers: [
    'contentGaps',
    'dataGaps',
    'customerDistrust',
    'actionGaps',
    'investigationRequired',
    'businessLogic',
  ],
  checkQuality: [
    'negativeAiAgentAnswerQuality',
    'highCustomerEffort',
    'negativeProductFeedback',
    'negativePolicyFeedback',
    'negativeStrongEmotions',
  ],
  increaseCoverage: ['channelGaps', 'audienceTargeting', 'scheduling', 'branchingLogic'],
};

const jobGroupObjects: JobGroupObject[] = [
  {
    label: 'components.reporting.automation.fin-insights.job-groups.improve-answers',
    header: 'components.reporting.automation.fin-insights.job-groups.headers.improve-answers',
    chartSeries: [{ metric_id: 'fin.resolution_rate' }],
    backgroundClass: 'bg-vis-green-50',
    borderClass: 'border-vis-green-50',
    fontClass: 'text-default',
    positiveLabel:
      'components.reporting.automation.fin-insights.jobs.metrics.positive-resolution-rate',
    negativeLabel:
      'components.reporting.automation.fin-insights.jobs.metrics.negative-resolution-rate',
    positiveTextColor: 'text-success',
    negativeTextColor: 'text-accent',
    positiveBackgroundClass: 'bg-vis-green-50',
    negativeBackgroundClass: 'bg-accent-fill',
    id: 'improveAnswers',
  },
  {
    label: 'components.reporting.automation.fin-insights.job-groups.check-quality',
    header: 'components.reporting.automation.fin-insights.job-groups.headers.check-quality',
    chartSeries: [
      {
        metric_id:
          'conversation.ai_generated_metrics.csat_score.first_user_conversation_part_created_at',
        filters: {
          type: 'and',
          filters: [
            {
              type: 'category',
              data: {
                property: 'agent_types_involved_grouping',
                attribute: 'conversation.agent_types_involved_grouping',
                values: ['ai-agent'],
              },
            },
          ],
        },
      },
    ],
    backgroundClass: 'bg-vis-violet-50',
    borderClass: 'border-vis-violet-50',
    fontClass: 'text-on-neutral',
    positiveLabel: 'components.reporting.automation.fin-insights.jobs.metrics.positive-cx-score',
    negativeLabel: 'components.reporting.automation.fin-insights.jobs.metrics.negative-cx-score',
    positiveTextColor: 'reporting__text-vis-violet-50',
    negativeTextColor: 'text-accent',
    positiveBackgroundClass: 'bg-vis-violet-50',
    negativeBackgroundClass: 'bg-accent-fill',
    id: 'checkQuality',
  },
  {
    label: 'components.reporting.automation.fin-insights.job-groups.increase-coverage',
    header: 'components.reporting.automation.fin-insights.job-groups.headers.increase-coverage',
    chartSeries: [{ metric_id: 'fin.participation_rate' }],
    backgroundClass: 'bg-vis-blue-50',
    borderClass: 'border-vis-blue-50',
    fontClass: 'text-on-neutral',
    positiveLabel:
      'components.reporting.automation.fin-insights.jobs.metrics.positive-involvement-rate',
    negativeLabel:
      'components.reporting.automation.fin-insights.jobs.metrics.negative-involvement-rate',
    positiveTextColor: 'reporting__text-vis-blue-50',
    negativeTextColor: 'text-accent',
    positiveBackgroundClass: 'bg-vis-blue-50',
    negativeBackgroundClass: 'bg-accent-fill',
    id: 'increaseCoverage',
  },
];
function jobs(intl: IntlService) {
  return jobDefinitions(intl) as Job[];
}

export { jobGroups, jobGroupObjects, jobs, type JobId, type Job, type JobGroupObject };
